import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Standardized Dropdown Component
 * @param {Object} props - Dropdown properties
 * @param {React.ReactNode} props.trigger - Element that triggers the dropdown
 * @param {React.ReactNode} props.children - Dropdown menu content
 * @param {boolean} props.isOpen - Controlled open state
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {string} props.position - Position: 'bottom-right', 'bottom-left', 'top-right', 'top-left', 'bottom', 'top'
 * @param {boolean} props.usePortal - Whether to render in portal (for z-index issues)
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.horizontalOffset - Horizontal offset in pixels (default: 8px for right-aligned, 0 for others)
 */
const Dropdown = ({
    trigger,
    children,
    isOpen: controlledIsOpen,
    onOpenChange,
    position = 'bottom-right',
    usePortal = false,
    className = '',
    horizontalOffset = 8
}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

    const setIsOpen = useCallback((value) => {
        if (!isControlled) {
            setInternalIsOpen(value);
        }
        onOpenChange?.(value);
    }, [isControlled, onOpenChange]);

    // Find first focusable element in dropdown
    const getFirstFocusableElement = useCallback(() => {
        if (!dropdownRef.current) return null;
        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ].join(', ');
        return dropdownRef.current.querySelector(focusableSelectors);
    }, []);

    // Focus management: focus first element when opening
    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                const firstFocusable = getFirstFocusableElement();
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [isOpen, getFirstFocusableElement]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
                // Return focus to trigger after closing
                if (triggerRef.current) {
                    triggerRef.current.focus();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('keydown', handleEscapeKey);
            };
        }
    }, [isOpen, setIsOpen]);

    // Position classes - using right-2 instead of right-0 for right-aligned positions to add horizontal offset
    const positionClasses = {
        'bottom-right': 'top-full mt-2 right-2',
        'bottom-left': 'top-full mt-2 left-0',
        'top-right': 'bottom-full mb-2 right-2',
        'top-left': 'bottom-full mb-2 left-0',
        'bottom': 'top-full mt-2 left-1/2 transform -translate-x-1/2',
        'top': 'bottom-full mb-2 left-1/2 transform -translate-x-1/2'
    };

    const dropdownContent = (
        <div
            ref={dropdownRef}
            className={`absolute ${positionClasses[position]} bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg shadow-xl z-[9999] ${className} ${
                isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
            } transition-all duration-200`}
            onClick={(e) => e.stopPropagation()}
            role="menu"
            aria-orientation="vertical"
        >
            {children}
        </div>
    );

    const handleTriggerKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
        } else if (e.key === 'Escape' && isOpen) {
            setIsOpen(false);
        }
    }, [isOpen, setIsOpen]);

    return (
        <div className="relative inline-block">
            <div 
                ref={triggerRef} 
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={handleTriggerKeyDown}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                aria-haspopup="menu"
            >
                {trigger}
            </div>
            {isOpen && (
                usePortal ? (
                    createPortal(
                        <div
                            className="fixed inset-0 z-[9998]"
                            onClick={() => setIsOpen(false)}
                        >
                            {(() => {
                                const rect = triggerRef.current?.getBoundingClientRect();
                                if (!rect) return null;
                                
                                // Calculate position with consistent offset (8px for top, horizontalOffset for right)
                                const isRightAligned = position === 'bottom-right' || position === 'top-right';
                                const verticalOffset = 8; // Consistent with mt-2 (8px)
                                // Add offset to pull dropdown leftward (larger right value = further from right edge)
                                const rightOffset = isRightAligned ? horizontalOffset : 0;
                                
                                const calculatedPosition = {
                                    top: position.includes('top') 
                                        ? rect.top - (rect.height || 0) - verticalOffset
                                        : rect.bottom + verticalOffset,
                                    right: isRightAligned 
                                        ? window.innerWidth - rect.right + rightOffset
                                        : window.innerWidth - rect.right,
                                    left: position === 'bottom-left' || position === 'top-left'
                                        ? rect.left
                                        : undefined
                                };
                                
                                return (
                                    <div
                                        ref={dropdownRef}
                                        className={`fixed bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg shadow-xl z-[9999] ${className}`}
                                        style={{
                                            top: calculatedPosition.top !== undefined ? `${calculatedPosition.top}px` : undefined,
                                            right: calculatedPosition.right !== undefined ? `${calculatedPosition.right}px` : undefined,
                                            left: calculatedPosition.left !== undefined ? `${calculatedPosition.left}px` : undefined
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        role="menu"
                                        aria-orientation="vertical"
                                    >
                                        {children}
                                    </div>
                                );
                            })()}
                        </div>,
                        document.body
                    )
                ) : (
                    dropdownContent
                )
            )}
        </div>
    );
};

export default Dropdown;

